import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Stack,
  ScrollArea,
  Text,
  Group,
  useMantineColorScheme,
  Pagination,
  TextInput,
  Loader,
  Center,
  Alert,
  Select,
  Badge,
  Paper,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconAlertCircle, IconUsers } from "@tabler/icons-react";
import TeamProfileCard from "components/TeamProfileCard";
import { useNavigate } from "react-router-dom";
import { useListTeamsQuery, TeamFilterInput, SortEnumType } from "generated/graphql";

/** Default number of items per page */
const DEFAULT_PAGE_SIZE = 20;

/** Available page size options */
const PAGE_SIZE_OPTIONS = [
  { value: "10", label: "10 per page" },
  { value: "20", label: "20 per page" },
  { value: "50", label: "50 per page" },
];

/** Filter options for active status */
const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All Teams" },
  { value: "active", label: "Active Only" },
  { value: "inactive", label: "Inactive Only" },
];

/**
 * TeamsOverview
 *
 * Displays a paginated, searchable list of teams using real GraphQL data.
 * Features:
 * - Server-side pagination with skip/take
 * - Debounced search filtering by name
 * - Active/inactive status filtering
 * - Team analytics summary
 * - Loading, error, and empty state handling
 * - Theme-aware styling
 *
 * @returns {JSX.Element} The TeamsOverview page component
 */
export default function TeamsOverview() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState("all");

  // Build filter based on search term and status
  const where: TeamFilterInput | undefined = useMemo(() => {
    const filters: TeamFilterInput[] = [];

    if (debouncedSearch.trim()) {
      filters.push({ name: { contains: debouncedSearch.trim() } });
    }

    if (statusFilter === "active") {
      filters.push({ active: { equals: true } });
    } else if (statusFilter === "inactive") {
      filters.push({ active: { equals: false } });
    }

    if (filters.length === 0) return undefined;
    if (filters.length === 1) return filters[0];
    return { and: filters };
  }, [debouncedSearch, statusFilter]);

  // Reset to page 1 when search or filter changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((value: string | null) => {
    if (value) {
      setStatusFilter(value);
      setPage(1);
    }
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((value: string | null) => {
    if (value) {
      setPageSize(parseInt(value, 10));
      setPage(1);
    }
  }, []);

  // GraphQL query with pagination and filtering
  const { data, loading, error } = useListTeamsQuery({
    variables: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      where,
      order: [{ name: SortEnumType.Asc }],
    },
    fetchPolicy: "cache-and-network",
  });

  const teams = data?.teams?.items ?? [];
  const totalCount = data?.teams?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Compute analytics from current page data
  const analytics = useMemo(() => {
    const totalPlayers = teams.reduce((sum, team) => sum + (team.players?.length ?? 0), 0);
    const activeTeams = teams.filter((team) => team.active).length;
    return { totalPlayers, activeTeams };
  }, [teams]);

  // Handle page change with bounds checking
  const handlePageChange = useCallback(
    (newPage: number) => {
      const safePage = Math.min(Math.max(1, newPage), totalPages);
      setPage(safePage);
    },
    [totalPages]
  );

  return (
    <Container fluid p={{ base: "sm", sm: "md" }}>
      <Stack gap="md">
        {/* Header with search, filters, and page size controls */}
        <Stack gap="sm">
          <Text fw={700} size="lg">
            Teams
          </Text>
          {/* Search and filter controls - stack on mobile, row on larger screens */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
            <TextInput
              placeholder="Search teams..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.currentTarget.value)}
              className="w-full sm:w-52"
            />
            <div className="flex gap-2 w-full sm:w-auto">
              <Select
                data={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="flex-1 sm:flex-none sm:w-32"
                size="sm"
              />
              <Select
                data={PAGE_SIZE_OPTIONS}
                value={pageSize.toString()}
                onChange={handlePageSizeChange}
                className="flex-1 sm:flex-none sm:w-32"
                size="sm"
              />
            </div>
          </div>
        </Stack>

        {/* Analytics summary - wrap on mobile */}
        {!loading && teams.length > 0 && (
          <Paper p="sm" withBorder>
            <div className="flex flex-wrap gap-3 sm:gap-6">
              <Group gap="xs">
                <IconUsers size={16} />
                <Text size="sm" c="dimmed">
                  Players:
                </Text>
                <Badge variant={isDark ? "filled" : "light"} color="blue">
                  {analytics.totalPlayers}
                </Badge>
              </Group>
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Active:
                </Text>
                <Badge variant={isDark ? "filled" : "light"} color="green">
                  {analytics.activeTeams} / {teams.length}
                </Badge>
              </Group>
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Total:
                </Text>
                <Badge variant={isDark ? "filled" : "light"} color="gray">
                  {totalCount}
                </Badge>
              </Group>
            </div>
          </Paper>
        )}

        {/* Loading state */}
        {loading && teams.length === 0 && (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        )}

        {/* Error state */}
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading teams"
            color="red"
            variant={isDark ? "filled" : "light"}
          >
            {error.message}
          </Alert>
        )}

        {/* Content */}
        {!loading || teams.length > 0 ? (
          <>
            <ScrollArea.Autosize mah="60vh">
              <Stack gap="md">
                {teams.map((team) => (
                  <div
                    key={team.id}
                    onClick={() => navigate(`/teams/${team.id}`)}
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/teams/${team.id}`);
                      }
                    }}
                  >
                    <TeamProfileCard
                      compact
                      logoUrl={team.franchise?.logoUrl ?? undefined}
                      teamName={team.name}
                      franchise={team.franchise?.name}
                      tier={team.active ? undefined : "Inactive"}
                    />
                  </div>
                ))}
                {/* Empty state */}
                {!loading && teams.length === 0 && !error && (
                  <Center py="xl">
                    <Text c={isDark ? undefined : "dimmed"}>
                      {debouncedSearch
                        ? `No teams found matching "${debouncedSearch}"`
                        : "No teams to display."}
                    </Text>
                  </Center>
                )}
              </Stack>
            </ScrollArea.Autosize>

            {/* Pagination controls - stack on mobile */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-4">
                <Text size="sm" c="dimmed" className="text-center sm:text-left">
                  Showing {Math.min((page - 1) * pageSize + 1, totalCount)} -{" "}
                  {Math.min(page * pageSize, totalCount)} of {totalCount} teams
                </Text>
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={handlePageChange}
                  size="sm"
                  className="justify-center sm:justify-end"
                />
              </div>
            )}
          </>
        ) : null}
      </Stack>
    </Container>
  );
}
